import React, { useEffect } from 'react';
import { Box, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { ReviewBatch } from '../../api/services';
import { CVD_BLUE_TEXT, CVD_ORANGE_TEXT } from '../../utils/cvdPalette';
import { byName, shortDay, ViewRow, whySentence } from './batch';

// The Update-maxes table (sc-6441 UI AC 4/6). One job per cell: number cells
// hold the number only, centered; who and when are separate columns; a note
// opens in a row of its own; no chips. Fixed column widths (table-layout:
// fixed) so typing, stepping, resetting or unchecking never moves a column.
// The header row is frozen at every width: MUI stickyHeader. On wide screens
// the container's overflow is off, so the header pins to the window (under the
// app bar). Narrower (laptops at 1280/1366, where the table doesn't fit beside
// the drawer) the container scrolls both ways, at most one window tall, so the
// header pins to the container's top while the table scrolls sideways.

interface Props {
  batch: ReviewBatch;
  rows: ViewRow[];
  groups: [ViewRow['kind'], string][];
  readOnly: boolean;
  cadenceWeeks: number; // the chosen restock cadence (the Why sentences follow it)
  refDay: string; // the review's day (dates in another year carry the year)
  drafts: Record<number, string>; // in-progress text in a New max box (e.g. cleared)
  notes: Record<number, string>;
  focusNote: number | null; // a row just unchecked: its note field takes focus
  onValue: (pe: number, value: number | null) => void; // null = back to the starting number
  onDraft: (pe: number, draft: string | null) => void;
  onInclude: (pe: number, included: boolean) => void;
  onNote: (pe: number, note: string) => void;
}

const MUTED = 'text.secondary';
const EXCLUDED = 'text.disabled';
const WIDE = '@media (min-width: 1400px)'; // the table (min 1080px) fits beside the drawer: no inner scroll box
const APP_BAR = 64;
// Wide: the window scrolls, so the frozen header sits under the fixed AppBar.
// Narrow: the container scrolls (both axes) and the header stays at its top.
const STICKY_TOP = { top: 0, [WIDE]: { top: APP_BAR } };

// [key, width] — null width = the Item column takes the rest.
const EDIT_COLS: [string, number | null][] = [
  ['inc', 36], ['item', null], ['who', 88], ['when', 60], ['now', 70], ['new', 166], ['eng', 76],
  ['strip', 124], ['why', 212], ['sku', 112],
];
const READ_COLS = EDIT_COLS.filter(([k]) => k !== 'inc' && k !== 'new');

export const WeeklyStrip: React.FC<{ weeks: number[] }> = ({ weeks }) => {
  const max = Math.max(1, ...weeks);
  const bw = 3;
  const gap = 1;
  const h = 20;
  const width = weeks.length * (bw + gap);
  return (
    <svg width={width} height={h} viewBox={`0 0 ${width} ${h}`} role="img"
      aria-label={`Units used each week, last ${weeks.length} weeks`} style={{ display: 'block', margin: '0 auto' }}>
      {weeks.map((v, i) => {
        if (v <= 0) return <rect key={i} x={i * (bw + gap)} y={h - 1} width={bw} height={1} fill="#c9ced6" />;
        const bh = Math.max(2, Math.round((v / max) * h));
        return <rect key={i} x={i * (bw + gap)} y={h - bh} width={bw} height={bh} fill="#6f7b8c" />;
      })}
    </svg>
  );
};

const stepButton = {
  height: 30, minWidth: 28, width: 28, p: 0, border: 1, borderColor: 'grey.400', borderRadius: 1,
  bgcolor: 'background.paper', color: 'text.primary', fontSize: 18, lineHeight: 1, cursor: 'pointer',
  '&:disabled': { opacity: 0.45, cursor: 'not-allowed' },
} as const;

const Stepper: React.FC<{
  row: ViewRow; disabled: boolean; draft: string | undefined;
  onValue: Props['onValue']; onDraft: Props['onDraft'];
}> = ({ row, disabled, draft, onValue, onDraft }) => {
  const pe = row.product_equivalency_id;
  const name = row.name ?? 'this item';
  const step = (d: number) => onValue(pe, Math.max(0, (row.value ?? row.max_now ?? 0) + d));
  const color = row.value == null || row.max_now == null || row.value === row.max_now
    ? 'text.primary' : row.value > row.max_now ? CVD_BLUE_TEXT : CVD_ORANGE_TEXT;
  return (
    // The empty first track mirrors the reset button's, so the number box sits
    // exactly under the column header.
    <Box sx={{ display: 'inline-grid', gridTemplateColumns: '22px 28px 44px 28px 22px', gap: '3px', alignItems: 'center' }}>
      <span aria-hidden="true" />
      <Box component="button" type="button" sx={stepButton} aria-label={`Lower the new max for ${name}`}
        disabled={disabled || row.value === 0} onClick={() => step(-1)}>−</Box>
      <Box
        component="input" type="text" inputMode="numeric" pattern="[0-9]*" id={`max-${pe}`}
        aria-label={`New max for ${name}`} placeholder="—" disabled={disabled}
        value={draft ?? (row.value == null ? '' : String(row.value))}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const digits = e.target.value.replace(/\D/g, '');
          if (digits === '') onDraft(pe, '');
          else { onDraft(pe, null); onValue(pe, Number(digits)); }
        }}
        // Clearing the box restores the starting number once you leave it.
        onBlur={() => { if (draft === '') { onDraft(pe, null); onValue(pe, null); } }}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            onDraft(pe, null);
            step(e.key === 'ArrowUp' ? 1 : -1);
          }
        }}
        sx={{
          width: 44, height: 30, p: '0 2px', textAlign: 'center', fontVariantNumeric: 'tabular-nums', fontWeight: 700,
          font: 'inherit', fontSize: 14, border: 1, borderColor: 'grey.400', borderRadius: 1, bgcolor: 'background.paper',
          color: disabled ? EXCLUDED : color, textDecoration: disabled ? 'line-through' : 'none',
          '&::placeholder': { color: 'text.secondary', fontWeight: 400 },
        }}
      />
      <Box component="button" type="button" sx={stepButton} aria-label={`Raise the new max for ${name}`}
        disabled={disabled} onClick={() => step(1)}>+</Box>
      <Box component="button" type="button" title="Back to the engine's number"
        aria-label={`Back to the engine's number for ${name}`} aria-hidden={!row.custom || disabled}
        tabIndex={row.custom && !disabled ? 0 : -1}
        onClick={() => { onDraft(pe, null); onValue(pe, null); document.getElementById(`max-${pe}`)?.focus(); }}
        sx={{
          ...stepButton, width: 22, minWidth: 22, borderColor: 'transparent', color: CVD_BLUE_TEXT, fontSize: 17,
          visibility: row.custom && !disabled ? 'visible' : 'hidden', // its space is always reserved
        }}>↺</Box>
    </Box>
  );
};

export const BatchTable: React.FC<Props> = ({
  batch, rows, groups, readOnly, cadenceWeeks, refDay, drafts, notes, focusNote, onValue, onDraft, onInclude, onNote,
}) => {
  useEffect(() => {
    if (focusNote != null) document.getElementById(`note-${focusNote}`)?.focus();
  }, [focusNote]);

  const cols = readOnly ? READ_COLS : EDIT_COLS;
  const applied = batch.applied;
  const head = (label: string, align: 'left' | 'center' = 'center') => (
    <TableCell align={align} sx={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em',
      color: MUTED, verticalAlign: 'bottom', ...STICKY_TOP, py: 1, px: 1.25, lineHeight: 1.3 }}>{label}</TableCell>
  );

  const body: React.ReactNode[] = [];
  groups.forEach(([kind, label]) => {
    const inGroup = rows.filter((r) => r.kind === kind).sort(byName);
    if (!inGroup.length) return;
    body.push(
      <TableRow key={`g-${kind}`}>
        <TableCell colSpan={cols.length} sx={{ bgcolor: 'grey.100', fontWeight: 500, py: 0.75 }}>
          {label}
          <Box component="span" sx={{ color: MUTED, fontWeight: 400, ml: 0.75 }}>{inGroup.length}</Box>
        </TableCell>
      </TableRow>,
    );
    inGroup.forEach((r) => {
      const pe = r.product_equivalency_id;
      const kept = !readOnly && r.proposed && !r.included;
      const cellSx = { verticalAlign: 'middle', px: 1.25, py: 1, fontSize: 14, color: kept ? EXCLUDED : 'text.primary',
                       borderBottom: kept ? 0 : undefined };
      const who = r.kind === 'applied' && applied ? applied.actor_name ?? applied.actor : r.set_by?.name;
      const when = r.kind === 'applied' && applied ? applied.applied_on : r.set_by?.on;
      body.push(
        <TableRow key={pe} data-testid={`batch-row-${pe}`}>
          {!readOnly && (
            <TableCell padding="none" align="center" sx={cellSx}>
              {r.proposed && (
                <Checkbox size="small" checked={r.included} inputProps={{ 'aria-label': `Include ${r.name ?? 'item'}` }}
                  onChange={(e) => onInclude(pe, e.target.checked)} />
              )}
            </TableCell>
          )}
          <TableCell sx={{ ...cellSx, fontWeight: kept ? 400 : 500, overflowWrap: 'anywhere' }}>{r.name ?? '—'}</TableCell>
          <TableCell align="center" sx={cellSx}>{who ?? '—'}</TableCell>
          <TableCell align="center" sx={{ ...cellSx, whiteSpace: 'nowrap' }}>{when ? shortDay(when, refDay) : '—'}</TableCell>
          <TableCell align="center" sx={{ ...cellSx, fontVariantNumeric: 'tabular-nums' }}>{r.max_now ?? '—'}</TableCell>
          {/* px 0.5: the 156px stepper fits the 166px column (at 0.75 it overflowed 2px, 1px right of its header). */}
          {!readOnly && (
            <TableCell align="center" sx={{ ...cellSx, px: 0.5 }}>
              <Stepper row={r} disabled={kept} draft={drafts[pe]} onValue={onValue} onDraft={onDraft} />
            </TableCell>
          )}
          <TableCell align="center" sx={{ ...cellSx, fontVariantNumeric: 'tabular-nums' }}>
            {(r.appliedRow?.engine_max ?? r.side.engine_max) ?? '—'}
          </TableCell>
          <TableCell align="center" sx={cellSx}><WeeklyStrip weeks={r.weeks} /></TableCell>
          <TableCell sx={{ ...cellSx, overflowWrap: 'anywhere' }}>{whySentence(r, batch, cadenceWeeks, refDay)}</TableCell>
          <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 11.5,
            color: kept ? EXCLUDED : MUTED, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.sku ?? '—'}</TableCell>
        </TableRow>,
      );
      if (kept) {
        const note = notes[pe] ?? '';
        body.push(
          <TableRow key={`note-${pe}`}>
            <TableCell sx={{ pt: 0 }} />
            <TableCell colSpan={cols.length - 1} sx={{ pt: 0 }}>
              <Box sx={{ display: 'grid', gap: 0.5, maxWidth: 640 }}>
                <Typography component="label" htmlFor={`note-${pe}`} sx={{ fontSize: 12, fontWeight: 500 }}>
                  Why keep it?{' '}
                  <Box component="span" sx={{ fontWeight: 400, color: MUTED }}>
                    Required.
                    {/* Only a recommendation is declined; a row proposed only by
                        the cadence change has none, so nothing holds it back. */}
                    {r.recommendation_id != null
                      && ` The engine won't propose this again for ${batch.windows.kept_weeks} weeks.`}
                  </Box>
                </Typography>
                <Box component="input" type="text" id={`note-${pe}`} value={note}
                  placeholder="e.g. Customer keeps 2 for the refinery turnaround"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onNote(pe, e.target.value)}
                  sx={{ font: 'inherit', fontSize: 14, px: 1, py: 0.6, borderRadius: 1, border: 1, width: '100%',
                        borderColor: note.trim() ? 'grey.400' : CVD_ORANGE_TEXT, bgcolor: 'background.paper' }} />
              </Box>
            </TableCell>
          </TableRow>,
        );
      }
    });
  });

  return (
    // Narrow: at most the window's height below the AppBar (less the page's
    // 24px padding), so scrolling the window to the table shows all of it and
    // the pinned header stays in view.
    <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper',
      overflow: 'auto', maxHeight: `calc(100vh - ${APP_BAR + 24}px)`,
      [WIDE]: { overflow: 'visible', maxHeight: 'none' } }}>
      {/* Line height only: a table-level `& td` fontSize would out-rank the
          cells' own sizes (11px headers, 11.5px SKU). Body cells are 14px. */}
      <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: 1080, width: '100%',
        '& td': { lineHeight: 1.45 } }}>
        <colgroup>
          {cols.map(([key, w]) => <col key={key} style={w == null ? undefined : { width: w }} />)}
        </colgroup>
        <TableHead>
          <TableRow>
            {!readOnly && <TableCell aria-label="Include" sx={STICKY_TOP} />}
            {head('Item', 'left')}
            {head('Max set by')}
            {head('Set on')}
            {head('Max now')}
            {!readOnly && head('New max')}
            {head("Engine's max")}
            {head(`Last ${batch.window_weeks} weeks`)}
            {head('Why')}
            {head('SKU')}
          </TableRow>
        </TableHead>
        <TableBody>{body}</TableBody>
      </Table>
    </TableContainer>
  );
};
